import { useCallback, useEffect, useMemo, useState } from 'react';
import NavBar from "../components/NavBar/NavBar";
// import GoalTracker from "../components/GoalTracker/GoalTracker";
import GoalModal from "../components/GoalModal/GoalModal";
import { CheckIcon, CancelIcon } from "../assets/icons/icons";
import "../components/GoalTracker/style.css";
import { supabase } from '../supabaseClient';
import { updateMyCurrentWeight } from '../api/profile';
import { Line, Bar } from 'react-chartjs-2';
import WeeklyCheckInCard from "../components/WeeklyCheckIn/WeeklyCheckInCard";
import LoadingAnimation from "../components/LoadingAnimation/LoadingAnimation";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

const getThemeBlueColor = () => {
  if (typeof window === 'undefined') return '#d4af37';
  const value = getComputedStyle(document.documentElement).getPropertyValue('--cl-blue').trim();
  return value || '#d4af37';
};

const withHexAlpha = (color, alphaHex) => {
  if (!color) return color;
  const trimmed = color.trim();
  if (/^#([0-9a-fA-F]{6})$/.test(trimmed)) {
    return `${trimmed}${alphaHex}`;
  }
  return color;
};

const GOAL_DEFAULT_TARGETS = {
  Once: 1,
  Daily: 7,
  Weekly: 4,
  Monthly: 30,
  Forever: 1,
};

const getGoalWindowStart = (frequency = '') => {
  const now = new Date();
  const anchor = new Date(now);
  if (frequency === 'Monthly') {
    anchor.setDate(anchor.getDate() - 30);
    return anchor.toISOString();
  }
  if (frequency === 'Weekly' || frequency === 'Daily') {
    anchor.setDate(anchor.getDate() - 7);
    return anchor.toISOString();
  }
  if (frequency === 'Once' || frequency === 'Forever') {
    anchor.setDate(anchor.getDate() - 3650);
    return anchor.toISOString();
  }
  anchor.setDate(anchor.getDate() - 7);
  return anchor.toISOString();
};

const getGoalLabelSuffix = (frequency = '') => {
  if (frequency === 'Monthly') return ' this month';
  if (frequency === 'Weekly' || frequency === 'Daily') return ' this week';
  return '';
};

const normalizeGoalTarget = (value, frequency) => {
  const parsed = parseInt(value, 10);
  if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  return GOAL_DEFAULT_TARGETS[frequency] || 1;
};

const resolveGoalTarget = (goal) => {
  if (!goal) return 1;
  return normalizeGoalTarget(goal.targetcount ?? goal.targetCount, goal.frequency);
};

const clampGoalPercent = (count, target) => {
  if (!target || target <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((count / target) * 100)));
};

const getLocalDateString = () => {
  const now = new Date();
  const tzOffset = now.getTimezoneOffset() * 60000;
  const localISO = new Date(now.getTime() - tzOffset).toISOString().split('T')[0];
  return localISO;
};

const toDateOnly = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' && value.includes('T') ? value.split('T')[0] : value;
  }
  return date.toISOString().split('T')[0];
};

const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function TrendChart({
  metric,
  records,
  weekStart,
  weekEnd,
  onPrevWeek,
  onNextWeek,
  disableNextWeek,
}) {
  const weekLabel = useMemo(() => {
    const startText = weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const endText = weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${startText} – ${endText}`;
  }, [weekStart, weekEnd]);

  const points = useMemo(() => {
    const start = new Date(weekStart);
    const list = [];
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(start);
      dayDate.setDate(start.getDate() + i);
      const key = dayDate.toISOString().split('T')[0];
      const matches = records.filter(r => toDateOnly(r.date || r.logdate) === key);
      let value = null;
      if (matches.length) {
        if (metric === 'steps') {
          value = matches.reduce((sum, item) => sum + (Number(item.steps) || 0), 0);
        } else {
          const latest = matches[matches.length - 1];
          value = Number(latest.weight ?? latest.value ?? latest?.metric_value ?? null);
        }
      }
      list.push({ label: dayLabels[i], value, dateKey: key });
    }
    return list;
  }, [records, metric, weekStart]);

  const chartData = useMemo(() => {
    const themeBlue = getThemeBlueColor();
    const themeBlueSoft = withHexAlpha(themeBlue, '33');
    const themeBlueFade = withHexAlpha(themeBlue, '0d');
    const labels = points.map(p => p.label);
    if (metric === 'steps') {
      return {
        labels,
        datasets: [
          {
            data: points.map(p => p.value ?? 0),
            backgroundColor: themeBlue,
            borderRadius: 20,
            barThickness: 20,
          },
        ],
      };
    }
    return {
      labels,
      datasets: [
        {
          data: points.map(p => (p.value != null ? Number(p.value.toFixed(1)) : null)),
          borderColor: themeBlue,
          borderWidth: 2.5,
          pointBackgroundColor: '#fff',
          pointBorderColor: themeBlue,
          pointBorderWidth: 2,
          pointRadius: 4,
          tension: 0.35,
          fill: true,
          backgroundColor: (ctx) => {
            const { chart } = ctx;
            const { ctx: c, chartArea } = chart || {};
            if (!chartArea) return 'rgba(47,126,189,0.2)';
            const grad = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            grad.addColorStop(0, themeBlueSoft);
            grad.addColorStop(1, themeBlueFade);
            return grad;
          },
        },
      ],
    };
  }, [points, metric]);

  const handleBarClick = useCallback((event, elements, chart) => {
    if (metric !== 'steps' || !chart) return;
    const active = (elements && elements.length)
      ? elements
      : chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
    if (!active.length) return;
    const native = event.native || {};
    chart.setActiveElements(active);
    chart.tooltip.setActiveElements(active, { x: native.offsetX || 0, y: native.offsetY || 0 });
    chart.update();
  }, [metric]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick: metric === 'steps' ? handleBarClick : undefined,
    plugins: {
      legend: { display: false },
      tooltip: {
        intersect: metric === 'steps',
        callbacks: {
          label: (ctx) => {
            const value = ctx.raw;
            if (metric === 'steps') {
              return `${Number(value || 0).toLocaleString()} steps`;
            }
            return value != null ? `${value} lbs` : 'No entry';
          },
        },
      },
    },
    interaction: {
      mode: metric === 'steps' ? 'nearest' : 'index',
      intersect: metric === 'steps',
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.08)', drawTicks: false },
        ticks: { color: '#cfd8f3', font: { size: 12 } },
      },
      y: {
        beginAtZero: metric === 'steps',
        grid: { color: 'rgba(255,255,255,0.08)' },
        ticks: {
          color: '#cfd8f3',
          callback: (val) => (metric === 'steps' ? `${val / 1000}k` : val),
        },
      },
    },
  }), [metric, handleBarClick]);

  const ChartComponent = metric === 'steps' ? Bar : Line;

  return (
    <div className="card chart-shell">
      <div className="chart-nav">
        <button type="button" className="chart-tab back" onClick={onPrevWeek} aria-label="Previous week">‹</button>
        <div className="chart-week">{weekLabel}</div>
        <button type="button" className="chart-tab forward" onClick={onNextWeek} disabled={disableNextWeek} aria-label="Next week">›</button>
      </div>
      <div className="chart-body">
        <div style={{ height: 260 }}>
          {records.length ? (
            <ChartComponent
              data={chartData}
              options={options}
            />
          ) : (
            <div className="chart-empty">No data tracked yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, unit = '', delta, variant = '' }) {
  const dir = delta > 0 ? 'up' : (delta < 0 ? 'down' : 'flat');
  const deltaText = (delta === null || delta === undefined || Number.isNaN(delta)) ? '--' : Math.abs(delta).toFixed(1);
  return (
    <div className={`target metric ${variant}`}>
      <div className="target-top">
        <div className="target-value">{value}{unit}</div>
      </div>
      <div className="target-label">{label}</div>
      <div className={`delta-pill ${dir}`}>
        {dir === 'up' ? '\u25B2' : dir === 'down' ? '\u25BC' : '\u2022'} {deltaText}
        <span className="delta-sub"> vs last week</span>
      </div>
    </div>
  );
}

export default function ProgressPage() {
  const [view, setView] = useState('main'); // 'main' | 'checkins'
  const [checkins, setCheckins] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  // goals state for overview
  const [goals, setGoals] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [expandActive, setExpandActive] = useState(true);
  const [expandCompleted, setExpandCompleted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [liftLogs, setLiftLogs] = useState([]);
  const [liftHistoryMap, setLiftHistoryMap] = useState({});
  const [liftRaw, setLiftRaw] = useState([]);
  const [selectedLift, setSelectedLift] = useState(null);
  const [liftPickerOpen, setLiftPickerOpen] = useState(false);
  const [liftSearch, setLiftSearch] = useState('');
  const [liftMetricsOpen, setLiftMetricsOpen] = useState(false);
  const [liftMetricsSearch, setLiftMetricsSearch] = useState('');
  const [metricsSelectedLift, setMetricsSelectedLift] = useState(null);
  const [liftRmMode, setLiftRmMode] = useState('est'); // 'est' | 'actual'
  const [dailyLogForm, setDailyLogForm] = useState(() => ({ weight: '', steps: '', logDate: getLocalDateString() }));
  const [savingDailyLog, setSavingDailyLog] = useState(false);
  const [dailyLogFeedback, setDailyLogFeedback] = useState(null);
  const [editingDailyLog, setEditingDailyLog] = useState(false);
  const [chartMetric, setChartMetric] = useState('weight');
  const [chartWeekOffset, setChartWeekOffset] = useState(0);

  const userId = user?.id || null;
  const themeBlue = useMemo(() => getThemeBlueColor(), []);
  const themeBlueFill = useMemo(() => withHexAlpha(themeBlue, '1f'), [themeBlue]);
  const themeBlueMuted = useMemo(() => withHexAlpha(themeBlue, '80'), [themeBlue]);
  const themeBlueMutedFill = useMemo(() => withHexAlpha(themeBlue, '14'), [themeBlue]);

  const normalizeCheckinRow = (row = {}) => ({
    date: row.createdat || row.created_at || row.date || null,
    weight: row.weight ?? null,
    energy: row.energy_rating ?? row.stress ?? null,
    digestion: row.digestion_rating ?? row.sleep ?? null,
    training: row.training_rating ?? null,
    wellbeing: row.wellbeing_rating ?? null,
    recovery: row.recovery_rating ?? null,
    mealConsistency: row.meal_consistency ?? null,
    workoutConsistency: row.workout_consistency ?? row.workouts_completed ?? null,
    painNotes: row.pain_notes ?? row.notes ?? null,
    wins: row.wins_summary ?? null,
  });

  const normalizeDailyLogRow = (row = {}) => ({
    id: row.id ?? `${row.userid || ''}-${row.logdate || row.log_date || row.createdat || row.created_at || ''}`,
    date: row.logdate || row.log_date || row.date || row.createdat || row.created_at || null,
    weight: row.weight ?? row.weight_lbs ?? null,
    steps: row.steps ?? row.step_count ?? null,
  });

  const toNumberOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const trimmed = `${value}`.trim();
    if (!trimmed.length) return null;
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  };

  const loadWeeklyCheckins = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const { data, error } = await supabase
        .from('weekly_checkins')
        .select('*')
        .eq('userid', uid)
        .order('createdat', { ascending: false })
        .limit(50);
      if (error) throw error;
      setCheckins((data || []).map(normalizeCheckinRow));
    } catch (err) {
      setCheckins([]);
    }
  }, []);

  const triggerCheckinReload = useCallback(() => {
    if (userId) {
      loadWeeklyCheckins(userId);
    }
  }, [userId, loadWeeklyCheckins]);

  const handleDailyLogSubmit = async (event) => {
    event.preventDefault();
    if (!userId) {
      setDailyLogFeedback({ type: 'error', message: 'Sign in to log your daily stats.' });
      return;
    }
    setSavingDailyLog(true);
    setDailyLogFeedback(null);
    const logDate = dailyLogForm.logDate || new Date().toISOString().split('T')[0];
    try {
      const payload = {
        userid: userId,
        logdate: logDate,
        weight: toNumberOrNull(dailyLogForm.weight),
        steps: toNumberOrNull(dailyLogForm.steps),
      };
      if (payload.weight == null) throw new Error('Daily weight is required.');
      const existing = dailyLogs.find(log => {
        const dateOnly = toDateOnly(log.date);
        return dateOnly === logDate;
      });
      let response;
      if (existing?.id) {
        response = await supabase
          .from('daily_logs')
          .update({ weight: payload.weight, steps: payload.steps, logdate: logDate })
          .eq('id', existing.id)
          .select()
          .single();
      } else {
        response = await supabase
          .from('daily_logs')
          .insert([payload])
          .select()
          .single();
      }
      const { data, error } = response;
      if (error) throw error;
      const normalized = normalizeDailyLogRow(data);
      setDailyLogs(prev => {
        const targetDate = toDateOnly(normalized.date);
        const withoutDate = prev.filter(log => toDateOnly(log.date) !== targetDate);
        const list = [normalized, ...withoutDate];
        return list.sort((a, b) => new Date(b.date) - new Date(a.date));
      });
      setDailyLogForm(prev => ({ ...prev, weight: '', steps: '' }));
      try {
        await updateMyCurrentWeight(payload.weight, userId);
      } catch (_) {
        // ignore weight sync failures
      }
      setDailyLogFeedback({ type: 'success', message: 'Daily log saved.' });
      setEditingDailyLog(false);
    } catch (err) {
      setDailyLogFeedback({ type: 'error', message: err.message || 'Unable to save your daily stats.' });
    } finally {
      setSavingDailyLog(false);
    }
  };

  const formatDateDisplay = (value) => {
    if (!value) return '--';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      const date = new Date(y, (m || 1) - 1, d || 1);
      return date.toLocaleDateString();
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const beginDailyLogEdit = () => {
    if (selectedDailyLog) {
      setDailyLogForm(prev => ({
        ...prev,
        weight: selectedDailyLog.weight === null || selectedDailyLog.weight === undefined ? '' : `${selectedDailyLog.weight}`,
        steps: selectedDailyLog.steps === null || selectedDailyLog.steps === undefined ? '' : `${selectedDailyLog.steps}`,
      }));
    }
    setEditingDailyLog(true);
    setDailyLogFeedback(null);
  };

  const loadGoalsForUser = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .eq('userid', uid)
        .order('createdat', { ascending: false });
      if (error) throw error;
      setGoals(Array.isArray(data) ? data : []);
    } catch (err) {
      setGoals([]);
    }
  }, []);

  const loadDailyLogs = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const { data, error } = await supabase
        .from('daily_logs')
        .select('*')
        .eq('userid', uid)
        .limit(30);
      if (error) throw error;
      const rows = (data || []).map(normalizeDailyLogRow).filter(r => r.date);
      rows.sort((a, b) => new Date(b.date) - new Date(a.date));
      setDailyLogs(rows);
    } catch (err) {
      setDailyLogs([]);
    }
  }, []);

  const loadWorkoutSetLogs = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const { data, error } = await supabase
        .from('workout_set_logs')
        .select('exercise_name, week_key, set_index, weight, reps, duration_seconds, done, updated_at')
        .eq('user_id', uid)
        .order('week_key', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(400);
      if (error) throw error;
      const latestMap = new Map();
      const history = {};
      const raw = [];
      (data || []).forEach((row) => {
        const key = row.exercise_name;
        const payload = {
          exercise: row.exercise_name,
          weight: row.weight != null ? Number(row.weight) : null,
          reps: row.reps != null ? Number(row.reps) : null,
          duration: row.duration_seconds != null ? Number(row.duration_seconds) : null,
          week: row.week_key,
          updatedAt: row.updated_at || row.week_key,
          setIndex: row.set_index,
          done: row.done ?? null,
        };
        raw.push(payload);
        if (!history[key]) history[key] = [];
        history[key].push(payload);
        const existing = latestMap.get(key);
        const nextDate = new Date(payload.updatedAt);
        if (!existing || nextDate > new Date(existing.updatedAt)) {
          latestMap.set(key, payload);
        }
      });
      Object.keys(history).forEach((k) => {
        history[k].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      });
      const latest = Array.from(latestMap.values()).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      setLiftLogs(latest);
      setLiftHistoryMap(history);
      setLiftRaw(raw);
      if (!selectedLift && latest.length) setSelectedLift(latest[0].exercise);
    } catch (err) {
      setLiftLogs([]);
      setLiftHistoryMap({});
      setLiftRaw([]);
    }
  }, [selectedLift]);

  useEffect(() => {
    let mounted = true;
    const bootstrap = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!mounted) return;
        const currentUser = auth?.user ?? null;
        setUser(currentUser);
        if (!currentUser?.id) {
          setGoals([]);
          setDailyLogs([]);
          setCheckins(sampleCheckins());
          return;
        }
        await Promise.all([
          loadWeeklyCheckins(currentUser.id),
          loadGoalsForUser(currentUser.id),
          loadDailyLogs(currentUser.id),
          loadWorkoutSetLogs(currentUser.id),
        ]);
      } catch (err) {
        if (mounted) {
          setUser(null);
          setGoals([]);
          setDailyLogs([]);
          setLiftLogs([]);
          setCheckins(sampleCheckins());
        }
      } finally {
        if (mounted) setLoadingUser(false);
      }
    };
    bootstrap();
    return () => { mounted = false; };
  }, [loadWeeklyCheckins, loadGoalsForUser, loadDailyLogs, loadWorkoutSetLogs]);

  const chartRecords = useMemo(() => {
    if (chartMetric === 'steps') return dailyLogs;
    return dailyLogs.length ? dailyLogs : checkins;
  }, [chartMetric, dailyLogs, checkins]);
  const selectedDateKey = useMemo(() => toDateOnly(dailyLogForm.logDate), [dailyLogForm.logDate]);
  const selectedDailyLog = useMemo(() => {
    if (!selectedDateKey) return null;
    return dailyLogs.find(log => toDateOnly(log.date) === selectedDateKey) || null;
  }, [dailyLogs, selectedDateKey]);
  const showDailyLogForm = !selectedDailyLog || editingDailyLog;
  const hasStepsData = dailyLogs.some(log => Number(log.steps) > 0);
  const weekStart = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay() - (chartWeekOffset * 7));
    return start;
  }, [chartWeekOffset]);
  const weekEnd = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(weekStart.getDate() + 6);
    return end;
  }, [weekStart]);
  const latest = checkins[0] || {};
  const prev = checkins[1] || latest;
  const recentLifts = useMemo(() => liftLogs.slice(0, 6), [liftLogs]);
  const selectedLiftHistory = useMemo(() => {
    if (!selectedLift) return [];
    return liftHistoryMap[selectedLift] || [];
  }, [liftHistoryMap, selectedLift]);
  const getWeekKey = useCallback((dateLike) => {
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '';
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    return d.toISOString().split('T')[0];
  }, []);
  const liftCompareData = useMemo(() => {
    if (!selectedLiftHistory.length) return { labels: [], current: [], previous: [], currentLabel: '', previousLabel: '' };
    const weekGroups = new Map();
    selectedLiftHistory.forEach((entry) => {
      const wk = getWeekKey(entry.week || entry.updatedAt || entry.date || new Date());
      if (!wk) return;
      if (!weekGroups.has(wk)) weekGroups.set(wk, []);
      weekGroups.get(wk).push(entry);
    });
    const sortedWeeks = Array.from(weekGroups.keys()).sort((a, b) => new Date(b) - new Date(a));
    const currentWeekKey = sortedWeeks[0];
    const previousWeekKey = sortedWeeks[1] || null;
    const currentEntries = weekGroups.get(currentWeekKey) || [];
    const previousEntries = previousWeekKey ? weekGroups.get(previousWeekKey) || [] : [];
    const maxSet = Math.max(
      1,
      ...currentEntries.map((e) => (Number(e.setIndex) || 0) + 1),
      ...previousEntries.map((e) => (Number(e.setIndex) || 0) + 1)
    );
    const labels = Array.from({ length: maxSet }, (_, i) => `Set ${i + 1}`);
    const toSeries = (entries) => {
      const map = new Map();
      entries.forEach((e) => {
        const idx = (Number(e.setIndex) || 0);
        const weight = Number(e.weight);
        if (Number.isFinite(weight)) map.set(idx, weight);
      });
      return labels.map((_, i) => (map.has(i) ? map.get(i) : null));
    };
    return {
      labels,
      current: toSeries(currentEntries),
      previous: toSeries(previousEntries),
      currentLabel: currentWeekKey ? new Date(currentWeekKey).toLocaleDateString() : 'Current',
      previousLabel: previousWeekKey ? new Date(previousWeekKey).toLocaleDateString() : 'Previous',
    };
  }, [getWeekKey, selectedLiftHistory]);

  const liftMetricsList = useMemo(() => {
    const names = Object.keys(liftHistoryMap || {});
    return names
      .filter((n) => n.toLowerCase().includes(liftMetricsSearch.toLowerCase()))
      .sort((a, b) => a.localeCompare(b))
      .map((name) => {
        const history = liftHistoryMap[name] || [];
        let heaviest = 0;
        let best5 = 0;
        let best8 = 0;
        let best12 = 0;
        const oneRmByDate = new Map();
        const actualByDate = new Map();
        history.forEach((entry) => {
          const w = Number(entry.weight) || 0;
          const r = Number(entry.reps) || 0;
          if (w > heaviest) heaviest = w;
          if (r >= 5 && w > best5) best5 = w;
          if (r >= 8 && w > best8) best8 = w;
          if (r >= 12 && w > best12) best12 = w;
          if (w && r) {
            const est = Number((w * (1 + r / 30)).toFixed(1));
            const dayKey = (() => {
              const d = new Date(entry.week || entry.updatedAt || entry.date || new Date());
              if (Number.isNaN(d.getTime())) return null;
              d.setHours(0, 0, 0, 0);
              return d.toISOString().split('T')[0];
            })();
            if (dayKey) {
              const prev = oneRmByDate.get(dayKey) || 0;
              if (est > prev) oneRmByDate.set(dayKey, est);
              const prevActual = actualByDate.get(dayKey) || 0;
              if (w > prevActual) actualByDate.set(dayKey, w);
            }
          } else if (w) {
            const dayKey = (() => {
              const d = new Date(entry.week || entry.updatedAt || entry.date || new Date());
              if (Number.isNaN(d.getTime())) return null;
              d.setHours(0, 0, 0, 0);
              return d.toISOString().split('T')[0];
            })();
            if (dayKey) {
              const prevActual = actualByDate.get(dayKey) || 0;
              if (w > prevActual) actualByDate.set(dayKey, w);
            }
          }
        });
        const oneRm = Array.from(oneRmByDate.entries())
          .map(([date, value]) => ({ date, value }))
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-30);
        const actualOneRm = Array.from(actualByDate.entries())
          .map(([date, value]) => ({ date, value }))
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-30);
        return { name, heaviest, best5, best8, best12, oneRm, actualOneRm };
      });
  }, [liftHistoryMap, liftMetricsSearch]);
  const liftStats = useMemo(() => {
    const totalSets = Object.values(liftHistoryMap || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);
    const now = new Date();
    const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
    const recentSets = liftRaw.filter((r) => {
      const d = new Date(r.updatedAt || r.week);
      return d >= weekAgo;
    });
    const completedSets = recentSets.filter((r) => r.done === true).length;
    const timedSets = liftRaw.filter((r) => r.duration != null).length;
    const volumeThisWeek = recentSets.reduce((sum, r) => {
      const w = Number(r.weight) || 0;
      const reps = Number(r.reps) || 0;
      return sum + (w * reps);
    }, 0);
    const minutesThisWeek = recentSets.reduce((sum, r) => {
      const secs = Number(r.duration) || 0;
      return sum + secs / 60;
    }, 0);
    const dateKeys = new Set(liftRaw.map((r) => {
      const d = new Date(r.updatedAt || r.week || r.date || now);
      d.setHours(0, 0, 0, 0);
      return d.toISOString().split('T')[0];
    }));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let streak = 0;
    const cursor = new Date(today);
    while (dateKeys.has(cursor.toISOString().split('T')[0])) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }
    return {
      completionRate: totalSets ? Math.round((completedSets / totalSets) * 100) : 0,
      currentStreak: streak,
      volumeThisWeek,
      minutesThisWeek: Math.round(minutesThisWeek),
    };
  }, [liftHistoryMap, liftRaw]);

  const liftOptions = useMemo(() => {
    const names = Object.keys(liftHistoryMap || {});
    return names
      .filter((n) => n.toLowerCase().includes(liftSearch.toLowerCase()))
      .sort((a, b) => a.localeCompare(b))
      .map((name) => {
        const history = liftHistoryMap[name] || [];
        const latest = history[0] || {};
        return { name, week: latest.week || latest.updatedAt || '' };
      });
  }, [liftHistoryMap, liftSearch]);

  const selectedLift1RMPoints = useMemo(() => {
    if (!selectedLiftHistory.length) return [];
    return selectedLiftHistory
      .map((entry) => {
        const w = Number(entry.weight) || 0;
        const r = Number(entry.reps) || 0;
        if (!w || !r) return null;
        const est = Number((w * (1 + r / 30)).toFixed(1));
        return { date: entry.week || entry.updatedAt, value: est };
      })
      .filter(Boolean)
      .slice(0, 12)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [selectedLiftHistory]);

  // compute last week vs previous week average for change indicators
  const weekChanges = useMemo(() => {
    if (!checkins.length) return { weight: 0, energy: 0, training: 0, wellbeing: 0 };
    const byDate = checkins.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
    const end = new Date(byDate[byDate.length - 1].date);
    const start1 = new Date(end); start1.setDate(end.getDate() - 6);
    const start0 = new Date(end); start0.setDate(end.getDate() - 13);
    const inRange = (d, a, b) => d >= a && d <= b;
    const w1 = byDate.filter(c => inRange(new Date(c.date), start1, end));
    const w0 = byDate.filter(c => inRange(new Date(c.date), start0, new Date(start1.getTime() - 1)));
    const avg = (arr, k) => arr.length ? arr.reduce((s, x) => s + (Number(x[k]) || 0), 0) / arr.length : 0;
    return {
      weight: avg(w1, 'weight') - avg(w0, 'weight'),
      energy: avg(w1, 'energy') - avg(w0, 'energy'),
      training: avg(w1, 'training') - avg(w0, 'training'),
      wellbeing: avg(w1, 'wellbeing') - avg(w0, 'wellbeing'),
    };
  }, [checkins]);

  const filtered = useMemo(() => checkins.filter(c => {
    const s = `${c.painNotes || ''} ${c.wins || ''} ${c.weight || ''} ${c.energy || ''} ${c.training || ''} ${c.wellbeing || ''} ${c.mealConsistency || ''}`.toLowerCase();
    return s.includes(query.toLowerCase());
  }), [checkins, query]);

  // Build progress map for goals (for preview)
  useEffect(() => {
    const loadProgress = async () => {
      try {
        if (!userId || goals.length === 0) return;
        const updates = {};
        for (const g of goals) {
          const target = resolveGoalTarget(g);
          const fromISO = getGoalWindowStart(g.frequency);
          const fromDate = fromISO ? new Date(fromISO) : null;
          let query = supabase
            .from('goal_progress')
            .select('id, goalid, createdat, amount')
            .eq('userid', userId)
            .eq('goalid', g.id);
          if (fromISO) {
            query = query.gte('createdat', fromISO);
          }
          const { data, error } = await query;
          let count = 0;
          if (!error && Array.isArray(data)) {
            count = data.reduce((sum, row) => sum + (row?.amount ?? 1), 0);
          } else if (typeof window !== 'undefined') {
            const key = `goal_progress_cache_${userId}`;
            const cache = JSON.parse(window.localStorage.getItem(key) || '[]');
            count = cache
              .filter(r => r.goalid === g.id && (!fromDate || new Date(r.createdat) >= fromDate))
              .reduce((sum, row) => sum + (row?.amount ?? 1), 0);
          }
          const percent = clampGoalPercent(count, target);
          const suffix = getGoalLabelSuffix(g.frequency);
          const label = `${count}/${target}${suffix ? suffix : ''}`;
          updates[g.id] = { count, target, percent, label };
        }
        setProgressMap(updates);
      } catch {
        // ignore progress errors to keep rest of page usable
      }
    };
    loadProgress();
  }, [goals, userId]);

  // goal actions for modal
  const toggleModal = () => { setIsModalOpen(v => !v); setSelectedGoal(null); };
  const addGoal = async (goal) => {
    if (!userId) return;
    const payload = {
      ...goal,
      targetcount: normalizeGoalTarget(goal.targetcount, goal.frequency),
      userid: userId,
      createdat: new Date(),
      status: true,
    };
    const { data, error } = await supabase
      .from('goals')
      .insert([payload])
      .select();
    if (!error && Array.isArray(data) && data[0]) setGoals(prev => [data[0], ...prev]);
  };
  const updateGoal = async (goal) => {
    const payload = {
      ...goal,
      targetcount: normalizeGoalTarget(goal.targetcount ?? goal.targetCount, goal.frequency),
    };
    const { data } = await supabase.from('goals').update(payload).eq('id', payload.id).select();
    if (Array.isArray(data) && data[0]) setGoals(prev => prev.map(g => g.id === payload.id ? data[0] : g));
  };
  const markGoalAsCompleted = async (goalId) => {
    const completedAt = new Date();
    const { data } = await supabase.from('goals').update({ status: false, completedat: completedAt }).eq('id', goalId).select();
    if (Array.isArray(data) && data[0]) setGoals(prev => prev.map(g => g.id === goalId ? data[0] : g));
  };

  const logGoalProgress = async (goal, amount = 1) => {
    if (!userId || !goal?.id) return;
    const now = new Date();
    try {
      await supabase.from('goal_progress').insert([{ goalid: goal.id, userid: userId, createdat: now, amount }]);
    } catch {
      if (typeof window !== 'undefined') {
        const key = `goal_progress_cache_${userId}`;
        const cache = JSON.parse(window.localStorage.getItem(key) || '[]');
        cache.push({ goalid: goal.id, createdat: now, amount });
        window.localStorage.setItem(key, JSON.stringify(cache));
      }
    }
    setProgressMap(prev => {
      const prevEntry = prev[goal.id] || {};
      const nextCount = (prevEntry.count || 0) + amount;
      const target = prevEntry.target || resolveGoalTarget(goal);
      const percent = clampGoalPercent(nextCount, target);
      const suffix = getGoalLabelSuffix(goal.frequency);
      const label = `${nextCount}/${target}${suffix ? suffix : ''}`;
      return { ...prev, [goal.id]: { ...prevEntry, count: nextCount, target, percent, label } };
    });
  };

  const getGoalProgressText = (goal) => {
    const entry = progressMap[goal.id];
    if (entry?.label) return entry.label;
    const suffix = getGoalLabelSuffix(goal.frequency);
    const target = resolveGoalTarget(goal);
    return `0/${target}${suffix ? suffix : ''}`;
  };

  const getGoalProgressPercent = (goal) => progressMap[goal.id]?.percent ?? (goal.status === false ? 100 : 0);

  const goalsActive = goals.filter(g => g.status !== false);
  const goalsCompleted = goals.filter(g => g.status === false);

  if (loadingUser) {
    return (
      <>
        <NavBar />
        <main className="web-view">
          <LoadingAnimation fullScreen />
        </main>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <main className="web-view">
        <div className="container">
          {view === 'main' ? (
            <>
              <div className="checkin-grid">
                <WeeklyCheckInCard onCompleted={triggerCheckinReload} />
                <div className={`daily-log-card ${showDailyLogForm ? '' : 'complete'}`}>
                  {showDailyLogForm ? (
                    <>
                      <div className="daily-log-head">
                        <div>
                          <h3>Daily Log</h3>
                          <p className="daily-log-sub">Keep your momentum alive with a quick daily check-in.</p>
                        </div>
                        <div className="daily-log-date">
                          <span>Today</span>
                          <strong>{formatDateDisplay(dailyLogForm.logDate)}</strong>
                        </div>
                      </div>
                      <form className="daily-log-form" onSubmit={handleDailyLogSubmit}>
                        <div className="daily-log-fields">
                          <label>
                            <span>Weight (lbs)</span>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={dailyLogForm.weight}
                              onChange={(e) => setDailyLogForm(prev => ({ ...prev, weight: e.target.value }))}
                              required
                              disabled={!userId || savingDailyLog}
                            />
                          </label>
                          <label>
                            <span>Steps</span>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={dailyLogForm.steps}
                              onChange={(e) => setDailyLogForm(prev => ({ ...prev, steps: e.target.value }))}
                              disabled={!userId || savingDailyLog}
                            />
                          </label>
                        </div>
                        <button type="submit" className="daily-log-submit" disabled={!userId || savingDailyLog}>
                          {savingDailyLog ? 'Saving...' : selectedDailyLog ? 'Update log' : 'Save daily log'}
                        </button>
                        {!userId && !loadingUser && (
                          <p className="muted small">Sign in to start a daily log.</p>
                        )}
                        {dailyLogFeedback && <p className={`form-feedback ${dailyLogFeedback.type}`}>{dailyLogFeedback.message}</p>}
                      </form>
                    </>
                  ) : (
                    <div className="daily-log-complete">
                      <CheckIcon className="daily-log-complete-icon" />
                      <div>
                        <p>Daily log complete</p>
                        <span>{formatDateDisplay(selectedDailyLog?.date)}</span>
                      </div>
                      <button type="button" className="daily-log-edit-btn" onClick={beginDailyLogEdit}>Edit entry</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="card">
                <div className="card-header card-row metrics-header">
                  <div>
                    <h3>Metrics</h3>
                    <p>Latest values with weekly change</p>
                  </div>
                  <button className="view-checkins-btn" onClick={() => setView('checkins')}>View all check-ins</button>
                </div>
                <div className="targets metrics">
                  <Metric label="Weight" value={latest.weight ?? '--'} unit=" lbs" delta={weekChanges.weight} variant="metric-weight" />
                  <Metric label="Energy" value={latest.energy ?? '--'} unit="/5" delta={weekChanges.energy} variant="metric-stress" />
                  <Metric label="Training" value={latest.training ?? '--'} unit="/5" delta={weekChanges.training} variant="metric-sleep" />
                  <Metric label="Wellbeing" value={latest.wellbeing ?? '--'} unit="/5" delta={weekChanges.wellbeing} variant="metric-workouts" />
                </div>
              </div>

                <div className="card workout-metrics-card">
                  <div className="card-header card-row metrics-header">
                    <div>
                      <h3>Workout Metrics</h3>
                      <p>Completion, streaks, volume, and time</p>
                    </div>
                    <button className="view-checkins-btn" onClick={() => { setLiftMetricsOpen(true); setMetricsSelectedLift(liftOptions[0]?.name || null); }}>
                      View all lift metrics
                    </button>
                  </div>
                <div className="targets metrics workout-metrics-row">
                  <div className="target metric">
                    <div className="target-top">
                      <div className="target-value">{liftStats.completionRate ?? 0}%</div>
                    </div>
                    <div className="target-label">Workout completion</div>
                    <div className="delta-pill flat">· Logged sets</div>
                  </div>
                  <div className="target metric">
                    <div className="target-top">
                      <div className="target-value">{liftStats.currentStreak ?? 0}</div>
                    </div>
                    <div className="target-label">Day streak</div>
                    <div className="delta-pill flat">· Consecutive days</div>
                  </div>
                  <div className="target metric">
                    <div className="target-top">
                      <div className="target-value">{liftStats.volumeThisWeek ? Math.round(liftStats.volumeThisWeek) : '--'} lb</div>
                    </div>
                    <div className="target-label">Volume this week</div>
                    <div className="delta-pill flat">· Weight × reps</div>
                  </div>
                  <div className="target metric">
                    <div className="target-top">
                      <div className="target-value">{liftStats.minutesThisWeek || 0}</div>
                    </div>
                    <div className="target-label">Minutes worked out</div>
                    <div className="delta-pill flat">· Week total</div>
                  </div>
                </div>
              </div>

              <div className="grid-2">
                <div className="chart-card">
                  <div className="chart-tabs">
                    <button
                      type="button"
                      className={chartMetric === 'weight' ? 'active' : ''}
                      onClick={() => setChartMetric('weight')}
                    >
                      Weight
                    </button>
                    <button
                      type="button"
                      className={chartMetric === 'steps' ? 'active' : ''}
                      onClick={() => setChartMetric('steps')}
                      disabled={!hasStepsData}
                    >
                      Steps
                    </button>
                  </div>
                  <TrendChart
                    metric={chartMetric}
                    records={chartRecords}
                    weekStart={weekStart}
                    weekEnd={weekEnd}
                    onPrevWeek={() => setChartWeekOffset(prev => prev + 1)}
                    onNextWeek={() => setChartWeekOffset(prev => Math.max(0, prev - 1))}
                    disableNextWeek={chartWeekOffset === 0}
                  />
                </div>
                <div className="recent-lifts card lift-card">
                  <div className="card-header card-row">
                    <div>
                      <h3 className="your-goals-title">Recent lifts</h3>
                      <p className="muted small">Select a lift to see this vs last week</p>
                    </div>
                    <div className="lift-select-wrapper">
                      <button
                        type="button"
                        className="lift-select-trigger"
                        onClick={() => setLiftPickerOpen((o) => !o)}
                      >
                        {selectedLift || 'Select a lift'}
                        <span className="chevron">{liftPickerOpen ? '▴' : '▾'}</span>
                      </button>
                      {liftPickerOpen && (
                        <div className="lift-select-popover">
                          <input
                            className="lift-search"
                            placeholder="Search lifts"
                            value={liftSearch}
                            onChange={(e) => setLiftSearch(e.target.value)}
                          />
                          <div className="lift-options">
                            {liftOptions.length ? liftOptions.map((opt, idx) => {
                              const active = selectedLift === opt.name;
                              return (
                                <button
                                  key={`${opt.name}-${idx}`}
                                  type="button"
                                  className={`lift-option ${active ? 'active' : ''}`}
                                  onClick={() => { setSelectedLift(opt.name); setLiftPickerOpen(false); }}
                                >
                                  <span className="lift-option-name">{opt.name}</span>
                                  <span className="lift-option-week">{opt.week ? new Date(opt.week).toLocaleDateString() : ''}</span>
                                </button>
                              );
                            }) : <div className="lift-option empty">No lifts</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedLift ? (
                    <div className="lift-chart compare">
                      <Line
                        data={{
                          labels: liftCompareData.labels,
                          datasets: [
                            {
                              label: `This week (${liftCompareData.currentLabel})`,
                              data: liftCompareData.current,
                              borderColor: themeBlue,
                              backgroundColor: themeBlueFill,
                              tension: 0.25,
                              fill: true,
                              borderWidth: 2,
                              pointRadius: 4,
                            },
                            {
                              label: `Last week (${liftCompareData.previousLabel || 'n/a'})`,
                              data: liftCompareData.previous,
                              borderColor: themeBlueMuted,
                              backgroundColor: themeBlueMutedFill,
                              tension: 0.25,
                              fill: true,
                              borderWidth: 2,
                              pointRadius: 4,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { position: 'bottom', labels: { color: '#334155' } } },
                          scales: {
                            x: { grid: { color: '#e5e7eb' }, ticks: { color: '#475569' } },
                            y: { grid: { color: '#e5e7eb' }, ticks: { color: '#475569' }, title: { display: true, text: 'Weight (lb)', color: '#475569' } }
                          }
                        }}
                        height={260}
                      />
                    </div>
                  ) : (
                    <div className="muted" style={{ padding: '12px 0' }}>Select a lift to see set-by-set chart.</div>
                  )}
                </div>
              </div>

              {liftMetricsOpen && (
                <div className="modal-overlay" onClick={() => setLiftMetricsOpen(false)}>
                  <div className="modal-content exercise-modal" style={{ width: '900px', maxWidth: '95%' }} onClick={(e) => e.stopPropagation()}>
                    <div className="goal-accordion-container">
                      <h3>Lift Metrics</h3>
                      <CancelIcon onClick={() => setLiftMetricsOpen(false)} className="cancel-icon" />
                    </div>
                    <div className="lift-metrics-layout vertical">
                      <div className="lift-metrics-header">
                        <input
                          className="lift-search"
                          placeholder="Search lifts"
                          value={liftMetricsSearch}
                          onChange={(e) => setLiftMetricsSearch(e.target.value)}
                        />
                        <div className="lift-options horizontal">
                          {liftMetricsList.length ? liftMetricsList.map((opt, idx) => {
                            const active = metricsSelectedLift === opt.name;
                            return (
                              <button
                                key={`${opt.name}-${idx}`}
                                type="button"
                                className={`lift-option ${active ? 'active' : ''}`}
                                onClick={() => setMetricsSelectedLift(opt.name)}
                              >
                                <span className="lift-option-name">{opt.name}</span>
                                <span className="lift-option-week">{opt.week ? new Date(opt.week).toLocaleDateString() : ''}</span>
                              </button>
                            );
                          }) : <div className="lift-option empty">No lifts</div>}
                        </div>
                      </div>

                      <div className="lift-metrics-panel">
                        {metricsSelectedLift ? (() => {
                          const data = liftMetricsList.find((item) => item.name === metricsSelectedLift);
                          if (!data) return <div className="muted">No data for this lift yet.</div>;
                          const series = liftRmMode === 'actual' ? data.actualOneRm : data.oneRm;
                          return (
                            <>
                              <div className="panel-header">
                                <h4>{data.name}</h4>
                                <p className="muted small">PRs and estimated 1RM</p>
                              </div>
                              <div className="targets metrics lift-metrics-row">
                                <div className="target metric">
                                  <div className="target-top"><div className="target-value">{data.heaviest || '--'} lb</div></div>
                                  <div className="target-label">Heaviest set</div>
                                </div>
                                <div className="target metric">
                                  <div className="target-top"><div className="target-value">{data.best5 || '--'} lb</div></div>
                                  <div className="target-label">Best 5-rep</div>
                                </div>
                                <div className="target metric">
                                  <div className="target-top"><div className="target-value">{data.best8 || '--'} lb</div></div>
                                  <div className="target-label">Best 8-rep</div>
                                </div>
                                <div className="target metric">
                                  <div className="target-top"><div className="target-value">{data.best12 || '--'} lb</div></div>
                                  <div className="target-label">Best 12-rep</div>
                                </div>
                              </div>
                              <div className="lift-tabs">
                                <button
                                  type="button"
                                  className={liftRmMode === 'est' ? 'active' : ''}
                                  onClick={() => setLiftRmMode('est')}
                                >
                                  Epley est. 1RM
                                </button>
                                <button
                                  type="button"
                                  className={liftRmMode === 'actual' ? 'active' : ''}
                                  onClick={() => setLiftRmMode('actual')}
                                >
                                  Actual max per day
                                </button>
                              </div>
                              <div className="lift-chart compare" style={{ marginTop: 12 }}>
                                {series.length ? (
                                  <Line
                                    data={{
                                      labels: series.map((p) => new Date(p.date).toLocaleDateString()),
                                      datasets: [{
                                        label: liftRmMode === 'actual' ? 'Heaviest set per day' : 'Est. 1RM',
                                        data: series.map((p) => p.value),
                                        borderColor: themeBlue,
                                        backgroundColor: themeBlueFill,
                                        tension: 0.25,
                                        fill: true,
                                        borderWidth: 2,
                                        pointRadius: 4,
                                      }]
                                    }}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      plugins: { legend: { display: false } },
                                      scales: {
                                        x: { grid: { color: '#e5e7eb' }, ticks: { color: '#475569' } },
                                        y: { grid: { color: '#e5e7eb' }, ticks: { color: '#475569' }, title: { display: true, text: 'Est. 1RM (lb)', color: '#475569' } }
                                      }
                                    }}
                                    height={220}
                                  />
                                ) : (
                                  <div className="muted">Log weight + reps to see 1RM over time.</div>
                                )}
                              </div>
                            </>
                          );
                        })() : <div className="muted">Select a lift to view metrics.</div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="goals-section">
                <div className="goals-panel">
                  <div className="goals-panel-header">
                    <h4>Active Standards</h4>
                    <span className="goals-count">{goalsActive.length}</span>
                  </div>
                  <div className="goals-scroll">
                    {goalsActive.length ? goalsActive.map(g => {
                      const progressLabel = getGoalProgressText(g);
                      const progressPercent = getGoalProgressPercent(g);
                      return (
                        <div key={g.id} className="goal-item" onClick={() => { setSelectedGoal(g); setIsModalOpen(true); }}>
                          <div className="goal-row">
                            <div className="goal-title">{g.title}</div>
                            <div className="goal-row-actions">
                              <div className="goal-meta">{progressLabel}</div>
                              <button
                                type="button"
                                className="goal-log-btn"
                                onClick={(e) => { e.stopPropagation(); logGoalProgress(g); }}
                              >
                                + Log
                              </button>
                              <CheckIcon className='goal-complete-icon' onClick={(e) => { e.stopPropagation(); markGoalAsCompleted(g.id); }} />
                            </div>
                          </div>
                          <div className="goal-progress">
                            <div
                              className="goal-progress-fill"
                              style={{ width: `${progressPercent}%` }}
                              onClick={(e) => { e.stopPropagation(); logGoalProgress(g); }}
                              title="Click to log progress"
                            />
                          </div>
                        </div>
                      );
                    }) : <div className="muted">No active goals yet</div>}
                  </div>
                </div>
                <div className="goals-panel">
                  <div className="goals-panel-header" onClick={() => setExpandCompleted(x => !x)}>
                    <h4>Completed Standards</h4>
                    <div className="goals-right">
                      <span className="goals-count">{goalsCompleted.length}</span>
                      <span className="chev">{expandCompleted ? '▾' : '▸'}</span>
                    </div>
                  </div>
                  {expandCompleted && (
                    <div className="goals-scroll">
                      {goalsCompleted.length ? goalsCompleted.map(g => (
                        <div key={g.id} className="goal-item completed" onClick={() => { setSelectedGoal(g); setIsModalOpen(true); }}>
                          <div className="goal-row">
                            <div className="goal-title">{g.title}</div>
                            <div className="goal-row-actions">
                              <div className="goal-meta">Completed {g.completedat ? new Date(g.completedat).toLocaleDateString() : ''}</div>
                            </div>
                          </div>
                        </div>
                      )) : <div className="muted">No completed goals yet</div>}
                    </div>
                  )}
                </div>
                <button className="gt-add" onClick={toggleModal}>Add New Standard</button>
              </div>
              <GoalModal isOpen={isModalOpen} closeModal={toggleModal} addGoal={addGoal} updateGoal={updateGoal} selectedGoal={selectedGoal} markComplete={markGoalAsCompleted} />
            </>
          ) : (
            <div className="checkins">
              <div className="card">
                <div className="card-header card-row"><h3>Check-Ins</h3><button className="view-checkins-btn" onClick={() => setView('main')}>Back to progress</button></div>
                <input className="search" placeholder="Search check-ins..." value={query} onChange={(e) => setQuery(e.target.value)} />
                <div className="list">
                  {filtered.map((c, i) => (
                    <div key={i} className={`list-item ${selected === i ? 'selected' : ''}`} onClick={() => setSelected(i)}>
                      <div>
                        <div className="item-title">{new Date(c.date).toLocaleDateString()}</div>
                        <div className="item-sub">Wt {c.weight ?? '--'} | Energy {c.energy ?? '--'}/5 | Training {c.training ?? '--'}/5 | Wellbeing {c.wellbeing ?? '--'}/5</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selected != null && filtered[selected] && (
                <div className="card">
                  <div className="card-header card-row details-header">
                    <div className="details-heading">
                      <h3>Details</h3>
                      <p>{new Date(filtered[selected].date).toLocaleString()}</p>
                    </div>
                    <button
                      type="button"
                      className="details-close-btn"
                      aria-label="Close details"
                      onClick={() => setSelected(null)}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="detail-grid">
                    <div className="detail-box">
                      <h4>Performance Snapshot</h4>
                      <p>Weight: {filtered[selected].weight ?? '--'} lb</p>
                      <p>Energy: {filtered[selected].energy ?? '--'}/5</p>
                      <p>Training: {filtered[selected].training ?? '--'}/5</p>
                    </div>
                    <div className="detail-box">
                      <h4>Nutrition & Recovery</h4>
                      <p>Meal plan consistency: {filtered[selected].mealConsistency || '--'}</p>
                      <p>Digestion: {filtered[selected].digestion ?? '--'}/5</p>
                      <p>Workout completion: {filtered[selected].workoutConsistency || '--'}</p>
                      <p>Recovery: {filtered[selected].recovery ?? '--'}/5</p>
                    </div>
                    <div className="detail-box">
                      <h4>Mindset & Notes</h4>
                      <p>Wellbeing: {filtered[selected].wellbeing ?? '--'}/5</p>
                      <p>Pain / injuries: {filtered[selected].painNotes || '--'}</p>
                      <p>Wins & challenges: {filtered[selected].wins || '--'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function sampleCheckins() {
  const base = new Date();
  const arr = [];
  for (let i = 0; i < 10; i++) {
    const d = new Date(base); d.setDate(base.getDate() - i * 3);
    arr.push({
      date: d.toISOString(),
      weight: 180 - i * 0.7,
      energy: Math.floor(Math.random() * 5) + 1,
      digestion: Math.floor(Math.random() * 5) + 1,
      training: Math.floor(Math.random() * 5) + 1,
      wellbeing: Math.floor(Math.random() * 5) + 1,
      recovery: Math.floor(Math.random() * 5) + 1,
      mealConsistency: ['All', 'Most', 'Some'][Math.floor(Math.random() * 3)],
      workoutConsistency: ['All', 'Most', 'Some', 'None'][Math.floor(Math.random() * 4)],
      painNotes: 'Knee felt tight mid-week.',
      wins: 'Hit PR on Thursday, travel on weekend.'
    });
  }
  return arr;
}

